import { useCallback, useEffect, useRef, useState } from 'react';
import { openCashSession, type CashReport, type LicenceStatus, type Signer } from '@makila/pos-core';
import { activate, ApiError, currentLicence, type DeviceState, hasModule, homeOf, identity, loadDevice } from './lib/device';
import { IdbTerminalStore, openPosDb } from './lib/idb-store';
import { startSync, type SyncStatus } from './lib/sync';
import { ActivationScreen } from './screens/Activation';
import { CashOpenScreen } from './screens/CashOpen';
import { LockScreen, type Operator } from './screens/Lock';
import { SaleScreen } from './screens/Sale';
import { KitchenScreen } from './screens/Kitchen';
import { TablesScreen } from './screens/Tables';
import { QueueScreen } from './screens/Queue';
import { AgendaScreen } from './screens/Agenda';
import { AccessScreen } from './screens/Access';
import { DoorScreen } from './screens/Door';
import { WorkOrdersScreen } from './screens/WorkOrders';
import { DeliveriesScreen, markStop, type Delivering } from './screens/Deliveries';
import { recordStop } from '@makila/pos-core';
import type { Customer } from '@makila/pos-core';
import type { Order } from '@makila/pos-core';
import { switchTo } from './lib/windows';

/** Sem toques nem teclas durante este tempo, a caixa pede o PIN outra vez. */
const IDLE_LOCK_MS = 5 * 60_000;

type Boot =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'activate'; store: IdbTerminalStore }
  | { kind: 'ready'; store: IdbTerminalStore; device: DeviceState; signer: Signer };

export function App() {
  const [boot, setBoot] = useState<Boot>({ kind: 'loading' });
  const [sync, setSync] = useState<SyncStatus | null>(null);
  const [licence, setLicence] = useState<LicenceStatus | null>(null);
  const syncRef = useRef<ReturnType<typeof startSync> | null>(null);
  const deviceRef = useRef<DeviceState | null>(null);
  const [operator, setOperator] = useState<Operator | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [lastZ, setLastZ] = useState<CashReport | null>(null);
  // Restaurante: mapa de mesas ou balcão; e este equipamento pode ser o ecrã da cozinha.
  // 'tables' = o ecrã inicial do negócio (mesas, fila ou agenda); 'queue' e 'agenda' quando não são o inicial.
  const [view, setView] = useState<'tables' | 'counter' | 'queue' | 'agenda' | 'door' | 'workorders'>('tables');
  // Entregas: a paragem que se está a cobrar no balcão.
  const [delivering, setDelivering] = useState<Delivering | null>(null);
  // Vindo das mesas pela barra lateral: o que abrir já no balcão (painel da caixa ou devolução).
  const [openOnStart, setOpenOnStart] = useState<'cash' | 'return' | null>(null);
  // Fila: a senha que se está a cobrar no balcão.
  const [charging, setCharging] = useState<Order | null>(null);
  // Entradas: o sócio que vem renovar a mensalidade.
  const [renewing, setRenewing] = useState<Customer | null>(null);
  const [kitchen, setKitchen] = useState(false);

  // Bloqueio por inactividade.
  useEffect(() => {
    if (!operator) return;
    let t = setTimeout(() => setOperator(null), IDLE_LOCK_MS);
    const poke = () => { clearTimeout(t); t = setTimeout(() => setOperator(null), IDLE_LOCK_MS); };
    for (const ev of ['pointerdown', 'keydown'] as const) addEventListener(ev, poke);
    return () => { clearTimeout(t); for (const ev of ['pointerdown', 'keydown'] as const) removeEventListener(ev, poke); };
  }, [operator]);

  useEffect(() => {
    (async () => {
      try {
        // Pede ao browser que não apague estes dados para libertar espaço.
        await navigator.storage?.persist?.().catch(() => false);
        const store = await IdbTerminalStore.open(await openPosDb());
        const d = await loadDevice(store);
        setBoot(d ? { kind: 'ready', store, ...d } : { kind: 'activate', store });
      } catch (e) {
        setBoot({ kind: 'error', message: (e as Error).message });
      }
    })();
  }, []);

  const ready = boot.kind === 'ready' ? boot : null;
  const refreshLicence = useCallback(async () => {
    if (ready) setLicence(await currentLicence(ready.store, deviceRef.current ?? ready.device));
  }, [ready]);

  useEffect(() => {
    if (!ready) return;
    setSessionId(ready.store.openSession?.id ?? null);
    void ready.store.kv<boolean>('kitchenMode').then((k) => setKitchen(!!k));
    deviceRef.current = ready.device;
    void refreshLicence();
    const s = startSync(ready.store, () => deviceRef.current!, (d) => {
      deviceRef.current = d;
      setBoot((b) => (b.kind === 'ready' ? { ...b, device: d } : b));
    }, (st) => { setSync(st); void refreshLicence(); });
    syncRef.current = s;
    return () => s.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready?.store]);

  if (boot.kind === 'loading') return <div className="grid h-full place-items-center text-muted">A abrir a caixa…</div>;
  if (boot.kind === 'error') {
    return (
      <div className="grid h-full place-items-center p-6 text-center">
        <div>
          <h1 className="text-xl font-bold">Não foi possível abrir a base de dados do terminal</h1>
          <p className="mt-2 text-muted">{boot.message}</p>
          <p className="mt-2 text-muted">Confirme que o browser não está em navegação privada.</p>
        </div>
      </div>
    );
  }
  if (boot.kind === 'activate') {
    return (
      <ActivationScreen
        onActivate={async (code) => {
          try {
            const device = await activate(boot.store, code);
            const d = await loadDevice(boot.store);
            if (d) setBoot({ kind: 'ready', store: boot.store, device, signer: d.signer });
            return null;
          } catch (e) {
            return e instanceof ApiError ? e.message : (e as Error).message;
          }
        }}
      />
    );
  }
  const companyName = boot.device.company.tradeName ?? boot.device.company.legalName;
  if (kitchen) {
    return <KitchenScreen store={boot.store} device={boot.device} sync={sync} onSyncNow={() => syncRef.current?.now()} onExit={() => { void boot.store.setKv('kitchenMode', false); setKitchen(false); }} />;
  }
  if (!operator) return <LockScreen store={boot.store} companyName={companyName} refreshKey={sync?.lastSyncAt} onUnlock={setOperator} />;
  if (!sessionId) {
    return (
      <CashOpenScreen
        operatorName={operator.name}
        lastZ={lastZ}
        onLock={() => setOperator(null)}
        onKitchen={hasModule(boot.device, 'kitchen') && operator.role === 'manager' ? () => { void boot.store.setKv('kitchenMode', true); setKitchen(true); } : undefined}
        onOpen={async (float) => {
          try {
            const s = await openCashSession(boot.store, identity(boot.device), { operatorId: operator.id, openingFloat: float });
            setSessionId(s.id);
            setLastZ(null);
            syncRef.current?.now();
            return null;
          } catch (e) { return (e as Error).message; }
        }}
      />
    );
  }
  // Só um gerente vê o atalho para a gestão; lá entra com o email (a sessão dura um dia sem uso).
  const onGestao = operator.role === 'manager' ? () => switchTo('/gestao') : undefined;
  const home = homeOf(boot.device);
  // Bar: as contas por nome vivem no mesmo ecrã das mesas (e partilham a comanda).
  const tabsHome = home === 'tabs' && hasModule(boot.device, 'tabs') && !!boot.device.company.modules;
  const tablesHome = home === 'tables' || tabsHome || (hasModule(boot.device, 'tables') && boot.device.company.businessType === 'restaurant');
  const hasQueue = hasModule(boot.device, 'queue') && !!boot.device.company.modules;
  const hasAgenda = hasModule(boot.device, 'agenda') && !!boot.device.company.modules;
  const queueHome = home === 'queue' && hasQueue;
  const agendaHome = home === 'agenda' && hasAgenda;
  const toCounter = (open?: 'cash' | 'return') => { setCharging(null); setRenewing(null); setDelivering(null); setOpenOnStart(open ?? null); setView('counter'); };
  const charge = (o: Order) => { setCharging(o); setOpenOnStart(null); setView('counter'); };
  const common = { store: boot.store, device: boot.device, licence, sync, operator, onGestao, onLock: () => setOperator(null), onSyncNow: () => syncRef.current?.now(), onCounter: toCounter, onCharge: charge };
  const accessHome = home === 'access' && hasModule(boot.device, 'access') && !!boot.device.company.modules;
  if (accessHome && view === 'tables') {
    return <AccessScreen {...common} onRenew={(c) => { setRenewing(c); setCharging(null); setOpenOnStart(null); setView('counter'); }} />;
  }
  if ((agendaHome && view === 'tables') || (hasAgenda && view === 'agenda')) {
    return <AgendaScreen {...common} onQueue={hasQueue ? () => setView('queue') : undefined} />;
  }
  if ((queueHome && view === 'tables') || (hasQueue && view === 'queue')) {
    return <QueueScreen {...common} signer={boot.signer} onAgenda={hasAgenda ? () => setView(agendaHome ? 'tables' : 'agenda') : undefined} />;
  }
  // Serviços: o quadro das ordens (ecrã inicial da lavandaria, oficina e reparações; na electrónica, pela barra).
  const hasWork = hasModule(boot.device, 'workorders') && !!boot.device.company.modules;
  const workHome = home === 'workorders' && hasWork;
  if ((workHome && view === 'tables') || (hasWork && view === 'workorders')) {
    return <WorkOrdersScreen {...common} signer={boot.signer} />;
  }
  // Distribuição: a lista do dia do motorista é o ecrã inicial.
  const deliveriesHome = home === 'deliveries' && hasModule(boot.device, 'routes') && !!boot.device.company.modules;
  if (deliveriesHome && view === 'tables') {
    return <DeliveriesScreen {...common} onDeliver={(d) => { setDelivering(d); setCharging(null); setRenewing(null); setOpenOnStart(null); setView('counter'); }} />;
  }
  // Bar com entradas: a porta (bilhetes, convidados, contador da noite).
  const hasDoor = hasModule(boot.device, 'tickets') && !!boot.device.company.modules;
  const toDoor = hasDoor ? () => { setCharging(null); setOpenOnStart(null); setView('door'); } : undefined;
  if (hasDoor && view === 'door') {
    return <DoorScreen {...common} onTables={tablesHome ? () => setView('tables') : undefined} />;
  }
  if (tablesHome && view === 'tables') {
    return (
      <TablesScreen
        store={boot.store} device={boot.device} signer={boot.signer} licence={licence} sync={sync} operator={operator} cashSessionId={sessionId}
        onGestao={onGestao}
        onCounter={(open) => { setOpenOnStart(open ?? null); setView('counter'); }} onLock={() => setOperator(null)} onSold={() => syncRef.current?.now()} onSyncNow={() => syncRef.current?.now()}
        onKitchen={hasModule(boot.device, 'kitchen') ? () => { void boot.store.setKv('kitchenMode', true); setKitchen(true); } : undefined}
        tabsHome={tabsHome}
        onDoor={toDoor}
      />
    );
  }
  return (
    <SaleScreen
      key={charging?.id ?? renewing?.id ?? (delivering ? `entrega-${delivering.customer.id}` : 'balcao')}
      initialCustomer={renewing ?? (delivering ? { id: delivering.customer.id, name: delivering.customer.name, nuit: delivering.customer.nuit } : null)}
      initialItems={delivering?.items}
      onDeliveries={deliveriesHome ? () => { setDelivering(null); setView('tables'); } : undefined}
      onIssued={delivering ? (doc) => {
        const d = delivering;
        void (async () => {
          await recordStop(boot.store, identity(boot.device), { routeId: d.routeId, customerId: d.customer.id, day: d.day, status: 'delivered', document: { seriesId: doc.seriesId, number: doc.number, fullNumber: doc.fullNumber }, operatorId: operator.id });
          await markStop(boot.store, d.day, d.customer.id, { status: 'delivered', document: doc.fullNumber, total: doc.grossTotal, at: new Date().toISOString() });
          syncRef.current?.now();
        })();
      } : undefined}
      onAccess={accessHome ? () => { setRenewing(null); setView('tables'); } : undefined}
      onTables={tablesHome ? () => { setOpenOnStart(null); setView('tables'); } : undefined}
      tablesLabel={tabsHome ? 'Contas' : undefined}
      onDoor={toDoor}
      onQueue={hasQueue ? () => { setCharging(null); setOpenOnStart(null); setView(queueHome ? 'tables' : 'queue'); } : undefined}
      onAgenda={hasAgenda ? () => { setCharging(null); setOpenOnStart(null); setView(agendaHome ? 'tables' : 'agenda'); } : undefined}
      onWorkOrders={hasWork ? () => { setCharging(null); setOpenOnStart(null); setView(workHome ? 'tables' : 'workorders'); } : undefined}
      fromOrder={charging}
      onGestao={onGestao}
      openOnStart={openOnStart}
      store={boot.store}
      device={boot.device}
      signer={boot.signer}
      licence={licence}
      sync={sync}
      operator={operator}
      cashSessionId={sessionId}
      onLock={() => setOperator(null)}
      onCashChanged={() => syncRef.current?.now()}
      onSessionClosed={(z) => { setSessionId(null); setLastZ(z); syncRef.current?.now(); }}
      onSold={() => syncRef.current?.now()}
      onSyncNow={() => syncRef.current?.now()}
    />
  );
}
